// view.js
import React from 'react';
import {
  Box,
  CardContent,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import PlaceIcon from '@mui/icons-material/Place';
import WifiIcon from '@mui/icons-material/Wifi';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';

import {
  StyledMemberCard,
  MemberAvatarContainer,
  RoleBadge,
  MemberInfoSection,
} from './styled';

const MemberCardView = ({
  member,
  isAdmin = false,
  onMemberClick,
  onMenuOpen,
  onReviewMember,
  getTimeAgo,
  compact = false, // New prop for compact layout
}) => {
  const getStatusInfo = (status) => {
    switch (status) {
      case 'AC':
        return { label: 'Active', color: 'success', icon: <CheckCircleIcon /> };
      case 'PP':
        return { label: 'Pending', color: 'warning', icon: <PendingIcon /> };
      case 'RE':
        return { label: 'Rejected', color: 'error', icon: <CancelIcon /> };
      case 'IN':
        return { label: 'Inactive', color: 'default', icon: <PendingIcon /> };
      case 'BA':
        return { label: 'Banned', color: 'error', icon: <CancelIcon /> };
      default:
        return { label: 'Unknown', color: 'default', icon: <PendingIcon /> };
    }
  };

  const roleNames = {
    'FO': 'Founder',
    'AD': 'Admin',
    'MA': 'Manager',
    'PT': 'Partner',
    'EO': 'Event Organizer',
    'PA': 'Member',
    'GU': 'Guest'
  };

  const statusInfo = getStatusInfo(member.status);

  return (
    <StyledMemberCard
      onClick={() => onMemberClick && onMemberClick(member, 'profile')}
      compact={compact}
    >
      <CardContent sx={{ p: compact ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: compact ? 1 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <MemberAvatarContainer 
              className={member.isOnline && member.status === 'AC' ? 'online' : ''}
              compact={compact}
            >
              <Avatar
                src={member.avatar}
                className={member.isOnline && member.status === 'AC' ? 'online' : ''}
                sx={{ fontSize: compact ? '1.2rem' : '1.5rem' }}
              >
                {member.name ? member.name.charAt(0).toUpperCase() :
                  member.firstName ? member.firstName.charAt(0).toUpperCase() : '?'}
              </Avatar>
              {member.isVerified && (
                <VerifiedIcon
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    color: 'primary.main',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    fontSize: compact ? 14 : 18
                  }}
                />
              )}
            </MemberAvatarContainer>
          </Box>

          {!compact && isAdmin && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                color={statusInfo.color}
                label={statusInfo.label}
                icon={statusInfo.icon}
                variant={member.status === 'AC' ? 'filled' : 'outlined'}
              />
            </Box>
          )}
        </Box>

        <MemberInfoSection>
          <Typography className="member-name">
            {member.name || 'Unknown Member'}
          </Typography>

          {!compact && (
            <RoleBadge
              size="small"
              role={member.role}
              label={roleNames[member.role] || 'Member'}
              sx={{ mb: 1 }}
            />
          )}



          {!compact && member.bio && (
            <Typography className="member-bio">
              {member.bio}
            </Typography>
          )}

          {!compact && member.location && (
            <Box className="member-detail">
              <PlaceIcon />
              <Typography variant="body2">
                {member.location}
              </Typography>
            </Box>
          )}

          {!compact && (
            <Box className="member-detail">
              {(['FO', 'AD', 'MA', 'PT', 'EO'].includes(member.role)) ? (
                <>
                  <WorkspacePremiumIcon />
                  <Typography variant="body2">
                    Community {roleNames[member.role]}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    {member.status === 'AC' ?
                      `Member since ${dayjs(member.joinedDate).format('MMM YYYY')}` :
                      `Applied ${dayjs(member.joinedDate).format('MMM D')}`
                    }
                  </Typography>
                </>
              )}
            </Box>
          )}
        </MemberInfoSection>
      </CardContent>
    </StyledMemberCard>
  );
};

export default MemberCardView;